import { useTrackResourceView } from "@core/activities/hooks";
import { Resource } from "@core/shared/types";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
  type MouseEvent,
} from "react";
import type { YouTubeEvent, YouTubeProps } from "react-youtube";

const PLAYING_STATE = 1;
const SKIP_SECONDS = 10;
const CONTROLS_HIDE_DELAY = 2800;
const PSEUDO_FULLSCREEN_HISTORY_KEY = "__youtubePseudoFullscreen";

function extractYoutubeVideoId(url: string) {
  const match = url.match(
    /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/,
  );

  return match?.[1] ?? null;
}

/**
 * Owns the state and interactions for the platform-styled YouTube player.
 */
export function useYoutubePlayer(resource: Resource) {
  const { trackResourceView } = useTrackResourceView(resource);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(50);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPseudoFullscreen, setIsPseudoFullscreen] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const [isReady, setIsReady] = useState(false);
  const [hasError, setHasError] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<YouTubeEvent["target"] | null>(null);
  const controlsTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const progressIntervalRef = useRef<ReturnType<typeof setInterval>>();
  const isPseudoFullscreenRef = useRef(false);
  const ignoreNextPopStateRef = useRef(false);

  const videoId = useMemo(
    () => extractYoutubeVideoId(resource.url),
    [resource.url],
  );

  const opts: YouTubeProps["opts"] = useMemo(
    () => ({
      height: "100%",
      width: "100%",
      playerVars: {
        autoplay: 1,
        controls: 0,
        disablekb: 1,
        fs: 0,
        iv_load_policy: 3,
        modestbranding: 1,
        playsinline: 1,
        rel: 0,
      },
    }),
    [],
  );

  const clearControlsTimeout = useCallback(() => {
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
  }, []);

  const revealControls = useCallback(() => {
    setShowControls(true);
    clearControlsTimeout();

    if (isPlaying) {
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, CONTROLS_HIDE_DELAY);
    }
  }, [clearControlsTimeout, isPlaying]);

  const hideControls = useCallback(() => {
    clearControlsTimeout();

    if (isPlaying) {
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 500);
    }
  }, [clearControlsTimeout, isPlaying]);

  const stopProgressTracking = useCallback(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }
  }, []);

  const updateProgress = useCallback(async () => {
    const player = playerRef.current;

    if (!player) return;

    const [nextCurrentTime, nextDuration] = await Promise.all([
      player.getCurrentTime(),
      player.getDuration(),
    ]);

    setCurrentTime(nextCurrentTime);
    setDuration(nextDuration);
    setProgress(nextDuration > 0 ? (nextCurrentTime / nextDuration) * 100 : 0);
  }, []);

  const startProgressTracking = useCallback(() => {
    stopProgressTracking();
    void updateProgress();
    progressIntervalRef.current = setInterval(() => {
      void updateProgress();
    }, 500);
  }, [stopProgressTracking, updateProgress]);

  const onReady = useCallback(
    async (event: YouTubeEvent) => {
      playerRef.current = event.target;
      const [nextDuration, nextVolume, nextMutedState] = await Promise.all([
        event.target.getDuration(),
        event.target.getVolume(),
        event.target.isMuted(),
      ]);

      setDuration(nextDuration);
      setVolume(nextVolume);
      setIsMuted(nextMutedState);
      setIsReady(true);
      setHasError(false);
      trackResourceView();
    },
    [trackResourceView],
  );

  const onStateChange = useCallback(
    (event: YouTubeEvent<number>) => {
      const nextIsPlaying = event.data === PLAYING_STATE;
      setIsPlaying(nextIsPlaying);

      if (nextIsPlaying) {
        startProgressTracking();
        controlsTimeoutRef.current = setTimeout(() => {
          setShowControls(false);
        }, CONTROLS_HIDE_DELAY);
      } else {
        stopProgressTracking();
        setShowControls(true);
      }
    },
    [startProgressTracking, stopProgressTracking],
  );

  const onError = useCallback(() => {
    stopProgressTracking();
    setHasError(true);
    setIsReady(false);
    setIsPlaying(false);
    setShowControls(true);
  }, [stopProgressTracking]);

  const handlePlayPause = useCallback(async () => {
    const player = playerRef.current;

    if (!player || !isReady || hasError) return;

    try {
      if (isPlaying) {
        await player.pauseVideo();
      } else {
        await player.playVideo();
      }
    } catch {
      // The YouTube iframe can be torn down (navigation/unmount) while the
      // player wrapper still resolves; its internal player is then null.
    }
  }, [hasError, isPlaying, isReady]);

  const handleOverlayClick = () => {
    void handlePlayPause();
  };

  const handleOverlayKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === " " || event.key === "Enter") {
      event.preventDefault();
      void handlePlayPause();
    }
  };

  const handleMuteToggle = async () => {
    const player = playerRef.current;

    if (!player) return;

    if (isMuted) {
      await player.unMute();
      setVolume(await player.getVolume());
    } else {
      await player.mute();
    }

    setIsMuted(!isMuted);
  };

  const handleVolumeChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const player = playerRef.current;
    const nextVolume = Number(event.target.value);

    if (!player) return;

    await player.setVolume(nextVolume);
    setVolume(nextVolume);
    setIsMuted(nextVolume === 0);
  };

  const exitPseudoFullscreen = useCallback(() => {
    if (!isPseudoFullscreenRef.current) return;

    isPseudoFullscreenRef.current = false;
    setIsPseudoFullscreen(false);

    // Drop the history entry pushed on enter so "back" isn't needed twice.
    if (window.history.state?.[PSEUDO_FULLSCREEN_HISTORY_KEY]) {
      ignoreNextPopStateRef.current = true;
      window.history.back();
    }
  }, []);

  const enterPseudoFullscreen = useCallback(() => {
    if (isPseudoFullscreenRef.current) return;

    isPseudoFullscreenRef.current = true;
    setIsPseudoFullscreen(true);

    // Same URL, router state copied: the Remix router never sees this entry;
    // the capture-phase popstate listener below swallows its pops.
    try {
      window.history.pushState(
        {
          ...(window.history.state ?? {}),
          [PSEUDO_FULLSCREEN_HISTORY_KEY]: true,
        },
        "",
      );
    } catch {
      // History can be unavailable in sandboxed iframes; button/Escape still exit.
    }
  }, []);

  const handleFullscreenToggle = async () => {
    const container = containerRef.current as
      | (HTMLDivElement & { webkitRequestFullscreen?: () => Promise<void> })
      | null;

    if (!container) return;

    if (isPseudoFullscreenRef.current) {
      exitPseudoFullscreen();
      return;
    }

    const fullscreenDocument = document as Document & {
      webkitFullscreenElement?: Element | null;
      webkitFullscreenEnabled?: boolean;
      webkitExitFullscreen?: () => Promise<void>;
    };

    if (
      fullscreenDocument.fullscreenElement ||
      fullscreenDocument.webkitFullscreenElement
    ) {
      try {
        await (
          fullscreenDocument.exitFullscreen ??
          fullscreenDocument.webkitExitFullscreen
        )?.call(fullscreenDocument);
      } catch {
        // Already left fullscreen.
      }
      return;
    }

    // iOS Safari on iPhone exposes no element fullscreen API (iPadOS only the
    // webkit-prefixed one), so fall back to a CSS "pseudo-fullscreen".
    const requestFullscreen =
      container.requestFullscreen ?? container.webkitRequestFullscreen;
    const fullscreenEnabled =
      fullscreenDocument.fullscreenEnabled ??
      fullscreenDocument.webkitFullscreenEnabled;

    if (typeof requestFullscreen !== "function" || fullscreenEnabled === false) {
      enterPseudoFullscreen();
      return;
    }

    try {
      await requestFullscreen.call(container);
    } catch {
      // Fullscreen can be denied (iframe policy, no user gesture).
      enterPseudoFullscreen();
    }
  };

  const seekToPercentage = useCallback(
    async (percentage: number) => {
      const player = playerRef.current;

      if (!player || duration <= 0) return;

      const normalizedPercentage = Math.max(0, Math.min(100, percentage));
      const nextTime = (normalizedPercentage / 100) * duration;
      await player.seekTo(nextTime, true);
      setProgress(normalizedPercentage);
      setCurrentTime(nextTime);
    },
    [duration],
  );

  const handleProgressClick = (event: MouseEvent<HTMLDivElement>) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    const percentage = ((event.clientX - bounds.left) / bounds.width) * 100;
    void seekToPercentage(percentage);
  };

  const handleProgressKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;

    event.preventDefault();
    const change = event.key === "ArrowLeft" ? -5 : 5;
    void seekToPercentage(progress + change);
  };

  const handlePlaybackRateChange = async (rate: number) => {
    const player = playerRef.current;

    if (!player) return;

    await player.setPlaybackRate(rate);
    setPlaybackRate(rate);
  };

  const skipForward = async () => {
    const player = playerRef.current;

    if (!player || duration <= 0) return;

    const nextTime = Math.min(currentTime + SKIP_SECONDS, duration);
    await player.seekTo(nextTime, true);
    setCurrentTime(nextTime);
    setProgress((nextTime / duration) * 100);
  };

  const skipBackward = async () => {
    const player = playerRef.current;

    if (!player || duration <= 0) return;

    const nextTime = Math.max(currentTime - SKIP_SECONDS, 0);
    await player.seekTo(nextTime, true);
    setCurrentTime(nextTime);
    setProgress((nextTime / duration) * 100);
  };

  useEffect(() => {
    return () => {
      stopProgressTracking();
      clearControlsTimeout();
      playerRef.current = null;
    };
  }, [clearControlsTimeout, stopProgressTracking]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(
        Boolean(
          document.fullscreenElement ||
            (document as Document & { webkitFullscreenElement?: Element | null })
              .webkitFullscreenElement,
        ),
      );
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange,
      );
    };
  }, []);

  // Pseudo-fullscreen: lock page scroll, exit on Escape and on browser back.
  useEffect(() => {
    if (!isPseudoFullscreen) return;

    const root = document.documentElement;
    const previousOverflow = root.style.overflow;
    root.style.overflow = "hidden";

    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") {
        exitPseudoFullscreen();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      root.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [exitPseudoFullscreen, isPseudoFullscreen]);

  useEffect(() => {
    // Capture phase on window runs before the Remix router's popstate
    // listener. The pseudo-fullscreen entry has the same URL and router
    // state, so swallowing its pops keeps the router from re-running loaders.
    const handlePopState = (event: PopStateEvent) => {
      const enteringPseudoEntry = Boolean(
        event.state?.[PSEUDO_FULLSCREEN_HISTORY_KEY],
      );

      if (ignoreNextPopStateRef.current) {
        ignoreNextPopStateRef.current = false;
        event.stopImmediatePropagation();
        return;
      }

      if (isPseudoFullscreenRef.current && !enteringPseudoEntry) {
        // Back pressed while in pseudo-fullscreen.
        isPseudoFullscreenRef.current = false;
        setIsPseudoFullscreen(false);
        event.stopImmediatePropagation();
        return;
      }

      if (enteringPseudoEntry) {
        // Forward into a stale pseudo-fullscreen entry: same page, ignore.
        event.stopImmediatePropagation();
      }
    };

    window.addEventListener("popstate", handlePopState, true);

    return () => {
      window.removeEventListener("popstate", handlePopState, true);

      if (
        isPseudoFullscreenRef.current &&
        window.history.state?.[PSEUDO_FULLSCREEN_HISTORY_KEY]
      ) {
        isPseudoFullscreenRef.current = false;
        window.history.back();
      }
    };
  }, []);

  return {
    containerRef,
    currentTime,
    duration,
    handleFullscreenToggle,
    handleMuteToggle,
    handleOverlayClick,
    handleOverlayKeyDown,
    handlePlaybackRateChange,
    handlePlayPause,
    handleProgressClick,
    handleProgressKeyDown,
    handleVolumeChange,
    hasError: hasError || !videoId,
    hideControls,
    isFullscreen: isFullscreen || isPseudoFullscreen,
    isPseudoFullscreen,
    isMuted,
    isPlaying,
    isReady,
    onError,
    onReady,
    onStateChange,
    opts,
    playbackRate,
    progress,
    revealControls,
    showControls,
    skipBackward,
    skipForward,
    videoId,
    volume,
  };
}
